"use client";

import { getCartCookie } from "@/utils/cart/getCartCookie";
import { useMarket } from "@app/_context";
import { getCart } from "@server/requests/checkout/getCart";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useSyncExternalStore } from "react";
import useAddCartItem from "./cart/useAddCartItem";
import useUpdateCartItem from "./cart/useUpdateCartItem";
import useRemoveCartItem from "./cart/useRemoveCartItem";
import useClearCart from "./cart/useClearCart";
import useUpdateCartMetadata from "./cart/useUpdateCartMetadata";
import useAddPromotion from "./cart/useAddPromotion";
import useRemovePromotion from "./cart/useRemovePromotion";
import useAddGiftcard from "./cart/useAddGiftcard";
import useRemoveGiftcard from "./cart/useRemoveGiftcard";
import useUpdateDeliveryOption from "./cart/useUpdateDeliveryOption";
import { getCheckoutDelivery } from "@server/requests/checkout";
import { getGiftCard } from "@server/requests/giftcard/getGiftCard";
import { GIFTCARD_PAYMENT_METHOD_ID } from "@/config/checkout";

const subscribe = () => () => {};
const getClientSnapshot = () => true;
const getServerSnapshot = () => false;

function useIsClient() {
  return useSyncExternalStore(subscribe, getClientSnapshot, getServerSnapshot);
}

export default function useCartQuery() {
  const market = useMarket().state.market;
  const { serviceLocale: locale } = market;
  const isClient = useIsClient();

  const {
    clearCart,
    isErrorClearCart,
    isPendingClearCart,
    isSuccessClearCart,
  } = useClearCart();
  const {
    addCartItem,
    isErrorAddCartItem,
    isPendingAddCartItem,
    isSuccessAddCartItem,
  } = useAddCartItem();
  const {
    removeCartItem,
    isErrorRemoveCartItem,
    isPendingRemoveCartItem,
    isSuccessRemoveCartItem,
  } = useRemoveCartItem();
  const {
    updateCartItem,
    isErrorUpdateCartItem,
    isPendingUpdateCartItem,
    isSuccessUpdateCartItem,
  } = useUpdateCartItem();
  const {
    updateCartMetadata,
    isErrorUpdateCartMetadata,
    isPendingUpdateCartMetadata,
    isSuccessUpdateCartMetadata,
  } = useUpdateCartMetadata();

  const {
    addPromotion,
    isErrorAddPromotion,
    isPendingAddPromotion,
    isSuccessAddPromotion,
  } = useAddPromotion();
  const {
    removePromotion,
    isErrorRemovePromotion,
    isPendingRemovePromotion,
    isSuccessRemovePromotion,
  } = useRemovePromotion();

  const {
    addGiftcard,
    isErrorAddGiftcard,
    isPendingAddGiftcard,
    isSuccessAddGiftcard,
  } = useAddGiftcard();
  const {
    removeGiftcard,
    isErrorRemoveGiftcard,
    isPendingRemoveGiftcard,
    isSuccessRemoveGiftcard,
  } = useRemoveGiftcard();

  const {
    updateDeliveryOption,
    isErrorUpdateDeliveryOption,
    isPendingUpdateDeliveryOption,
    isSuccessUpdateDeliveryOption,
  } = useUpdateDeliveryOption();

  const {
    data: cart,
    isFetchedAfterMount,
    isError: isErrorGetCart,
    isPending: isPendingGetCart,
    isSuccess: isSuccessGetCart,
  } = useQuery({
    queryKey: ["cart"],
    enabled: isClient,
    queryFn: async () => {
      const cartCookie = getCartCookie();

      if (!cartCookie) {
        return null;
      }

      const cart = await getCart({
        cartId: cartCookie.value,
        locale,
      });

      return cart;
    },
  });

  const getDeliveryCheckout = async (zipCode: string) => {
    const cartCookie = getCartCookie();
    if (!cartCookie) return Promise.resolve(undefined);
    return getCheckoutDelivery({
      locale,
      cartId: cartCookie?.value ?? "",
      zipCode,
    });
  };

  const getGiftcard = async (giftCard: { cardNo: string; cvc: string }) => {
    const cartCookie = getCartCookie();
    if (!cartCookie) return;
    return getGiftCard({
      locale,
      cartId: cartCookie?.value ?? "",
      paymentMethodId: GIFTCARD_PAYMENT_METHOD_ID,
      giftCard,
    });
  };

  const loading = useMemo(
    () => ({
      getCart: isPendingGetCart,
      addCartItem: isPendingAddCartItem,
      addPromotion: isPendingAddPromotion,
      removePromotion: isPendingRemovePromotion,
      updateCartItem: isPendingUpdateCartItem,
      removeCartItem: isPendingRemoveCartItem,
      clearCart: isPendingClearCart,
      updateCartMetadata: isPendingUpdateCartMetadata,
      updateDeliveryOption: isPendingUpdateDeliveryOption,
      addGiftcard: isPendingAddGiftcard,
      removeGiftcard: isPendingRemoveGiftcard,
    }),
    [
      isPendingGetCart,
      isPendingAddCartItem,
      isPendingAddPromotion,
      isPendingRemovePromotion,
      isPendingUpdateCartItem,
      isPendingRemoveCartItem,
      isPendingClearCart,
      isPendingUpdateCartMetadata,
      isPendingUpdateDeliveryOption,
      isPendingAddGiftcard,
      isPendingRemoveGiftcard,
    ],
  );

  const error = useMemo(
    () => ({
      getCart: isErrorGetCart,
      addCartItem: isErrorAddCartItem,
      addPromotion: isErrorAddPromotion,
      removePromotion: isErrorRemovePromotion,
      updateCartItem: isErrorUpdateCartItem,
      removeCartItem: isErrorRemoveCartItem,
      clearCart: isErrorClearCart,
      updateCartMetadata: isErrorUpdateCartMetadata,
      updateDeliveryOption: isErrorUpdateDeliveryOption,
      addGiftcard: isErrorAddGiftcard,
      removeGiftcard: isErrorRemoveGiftcard,
    }),
    [
      isErrorGetCart,
      isErrorAddCartItem,
      isErrorAddPromotion,
      isErrorRemovePromotion,
      isErrorUpdateCartItem,
      isErrorRemoveCartItem,
      isErrorClearCart,
      isErrorUpdateCartMetadata,
      isErrorUpdateDeliveryOption,
      isErrorAddGiftcard,
      isErrorRemoveGiftcard,
    ],
  );

  const success = useMemo(
    () => ({
      getCart: isSuccessGetCart,
      addCartItem: isSuccessAddCartItem,
      addPromotion: isSuccessAddPromotion,
      removePromotion: isSuccessRemovePromotion,
      updateCartItem: isSuccessUpdateCartItem,
      removeCartItem: isSuccessRemoveCartItem,
      clearCart: isSuccessClearCart,
      updateCartMetadata: isSuccessUpdateCartMetadata,
      updateDeliveryOption: isSuccessUpdateDeliveryOption,
      addGiftcard: isSuccessAddGiftcard,
      removeGiftcard: isSuccessRemoveGiftcard,
    }),
    [
      isSuccessGetCart,
      isSuccessAddCartItem,
      isSuccessAddPromotion,
      isSuccessRemovePromotion,
      isSuccessUpdateCartItem,
      isSuccessRemoveCartItem,
      isSuccessClearCart,
      isSuccessUpdateCartMetadata,
      isSuccessUpdateDeliveryOption,
      isSuccessAddGiftcard,
      isSuccessRemoveGiftcard,
    ],
  );

  return {
    /** STATE */
    cart,
    error,
    loading,
    success,
    /** ACTIONS */
    addCartItem,
    addGiftcard,
    addPromotion,
    clearCart,
    removeCartItem,
    removeGiftcard,
    removePromotion,
    updateCartItem,
    updateCartMetadata,
    updateDeliveryOption,
    getGiftcard,
    getDeliveryCheckout,
    /** DERIVED */
    isEmpty: isFetchedAfterMount && !cart?.items.length,
    isError: Object.values(error).some(Boolean),
    isLoading: Object.values(loading).some(Boolean),
    isSuccess: Object.values(success).every(Boolean),
  };
}
